import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './config';
import { apiJson } from '../utils/apiFetch';
import '../css/system-monitor.css';

const WINDOWS = [1, 2, 3, 5];
const fmt = value => Number(value || 0).toLocaleString('th-TH', { maximumFractionDigits: 1 });
const timeText = value => value ? new Date(value).toLocaleTimeString('th-TH') : '-';

function SystemMonitor() {
  const [minutes, setMinutes] = useState(1);
  const [enabled, setEnabled] = useState(false);
  const [realtimeEnabled, setRealtimeEnabled] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(null);

  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError('');
    try {
      const result = await apiJson(`${API_BASE}/api/admin/system-monitor?minutes=${minutes}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        signal: controller.signal
      });
      setData(result);
    } catch (loadError) {
      if (loadError.name !== 'AbortError') setError(loadError.message || 'โหลดข้อมูลระบบไม่สำเร็จ');
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }, [minutes]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const timer = window.setInterval(load, realtimeEnabled ? 5000 : minutes * 60000);
    return () => {
      window.clearInterval(timer);
      requestRef.current?.abort();
    };
  }, [enabled, load, minutes, realtimeEnabled]);

  const stop = () => {
    requestRef.current?.abort();
    setEnabled(false);
    setRealtimeEnabled(false);
    setData(null);
    setError('');
  };

  return (
    <main className="sysmon-page">
      <header className="sysmon-header">
        <div><small>SYSTEM OBSERVABILITY</small><h1>System Monitor</h1><p>ดูโหลด API, Process, Socket และ Database แยกตามต้นทาง</p></div>
        <div className={`sysmon-state ${enabled ? 'is-on' : ''}`}><i />{enabled ? 'กำลังตรวจสอบ' : 'ปิดอยู่'}</div>
      </header>

      <section className="sysmon-control">
        <div><b>ช่วงข้อมูลและรอบ Refresh</b><span>ระบบจะไม่เรียก API จนกว่าจะกดเปิด</span></div>
        <div className="sysmon-window" role="group" aria-label="เลือกรอบรีเฟรช">
          {WINDOWS.map(value => <button key={value} className={minutes === value ? 'is-active' : ''} onClick={() => setMinutes(value)}>{value} นาที</button>)}
        </div>
        {!enabled
          ? <button className="sysmon-open" onClick={() => setEnabled(true)}>▶ เปิดการตรวจสอบ</button>
          : <div className="sysmon-actions"><button type="button" className={`sysmon-realtime-toggle ${realtimeEnabled ? 'is-on' : ''}`} aria-pressed={realtimeEnabled} onClick={() => setRealtimeEnabled(value => !value)}><i /> สรุป Real-time: {realtimeEnabled ? 'เปิด' : 'ปิด'}</button><button onClick={load} disabled={loading}>↻ {loading ? 'กำลังโหลด' : 'Refresh ตอนนี้'}</button><button className="is-stop" onClick={stop}>■ ปิด</button></div>}
      </section>

      {!enabled && <section className="sysmon-empty"><span>◉</span><h2>Monitor ยังไม่ทำงาน</h2><p>เลือก 1, 2, 3 หรือ 5 นาที แล้วกดเปิด เมื่อนั้นระบบจึงจะเริ่มเรียกข้อมูล</p></section>}
      {enabled && error && <div className="sysmon-error">⚠ {error}</div>}
      {enabled && data && <>
        <div className="sysmon-updated">ข้อมูลย้อนหลัง {data.windowMinutes} นาที · อัปเดตล่าสุด {timeText(data.generatedAt)} · {realtimeEnabled ? 'สรุป Real-time ทุก 5 วินาที' : `รอบถัดไปทุก ${minutes} นาที`}</div>
        <section className="sysmon-stats">
          <article><small>REQUESTS</small><strong>{fmt(data.summary?.requests)}</strong><span>ทั้งหมดในช่วงนี้</span></article>
          <article className={data.summary?.errors ? 'is-danger' : ''}><small>ERRORS</small><strong>{fmt(data.summary?.errors)}</strong><span>HTTP 4xx / 5xx</span></article>
          <article><small>AVERAGE</small><strong>{fmt(data.summary?.averageMs)} ms</strong><span>P95 {fmt(data.summary?.p95Ms)} ms</span></article>
          <article className={data.summary?.slowRequests ? 'is-warn' : ''}><small>SLOW</small><strong>{fmt(data.summary?.slowRequests)}</strong><span>ตั้งแต่ 1 วินาที</span></article>
          <article><small>IN FLIGHT</small><strong>{fmt(data.inFlight)}</strong><span>กำลังประมวลผล</span></article>
        </section>
        <section className="sysmon-resources">
          <article><h2>Node Process</h2><dl><div><dt>RSS memory</dt><dd>{fmt(data.process?.rssMb)} MB</dd></div><div><dt>Heap</dt><dd>{fmt(data.process?.heapUsedMb)} / {fmt(data.process?.heapTotalMb)} MB</dd></div><div><dt>Uptime</dt><dd>{fmt((data.process?.uptimeSeconds || 0) / 60)} นาที</dd></div></dl></article>
          <article><h2>Socket.IO</h2><dl><div><dt>ผู้เชื่อมต่อ</dt><dd>{fmt(data.socket?.connected)}</dd></div><div><dt>Rooms</dt><dd>{fmt(data.socket?.rooms)}</dd></div></dl></article>
          <article><h2>MySQL Pool</h2><dl><div><dt>Connections</dt><dd>{fmt(data.database?.total)}</dd></div><div><dt>ว่าง</dt><dd>{fmt(data.database?.free)}</dd></div><div><dt>รอคิว</dt><dd className={data.database?.queued ? 'is-danger' : ''}>{fmt(data.database?.queued)}</dd></div></dl></article>
        </section>
        <section className="sysmon-panel">
          <header><div><h2>โหลดแยกตามต้นทาง API</h2><p>เรียงจากเวลาประมวลผลรวมมากที่สุด เพื่อเห็นชัดว่าโหลดมาจาก Route ใด</p></div><b>{data.routes?.length || 0} routes</b></header>
          <div className="sysmon-table-wrap"><table><thead><tr><th>ต้นทาง</th><th>เรียก</th><th>ผิดพลาด</th><th>เฉลี่ย</th><th>สูงสุด</th><th>เวลารวม</th><th>Status</th></tr></thead><tbody>
            {!data.routes?.length ? <tr><td colSpan="7" className="sysmon-none">ยังไม่มี Request ในช่วงที่เลือก</td></tr> : data.routes.map(route => <tr key={route.source}><td><code>{route.source}</code></td><td>{fmt(route.calls)}</td><td className={route.errors ? 'is-danger' : ''}>{fmt(route.errors)}</td><td>{fmt(route.averageMs)} ms</td><td>{fmt(route.maxMs)} ms</td><td><b>{fmt(route.totalMs)} ms</b></td><td>{Object.entries(route.statuses || {}).map(([status, count]) => <span className={Number(status) >= 400 ? 'is-bad' : ''} key={status}>{status}: {count}</span>)}</td></tr>)}
          </tbody></table></div>
        </section>
        <section className="sysmon-panel">
          <header><div><h2>Request ที่ผิดพลาดหรือช้า</h2><p>แสดง HTTP error และรายการที่ใช้เวลาอย่างน้อย 1 วินาทีล่าสุด</p></div></header>
          <div className="sysmon-problems">{!data.recentProblems?.length ? <p className="sysmon-none">ไม่พบรายการผิดพลาดหรือช้า</p> : data.recentProblems.map((item, index) => <article key={`${item.timestamp}-${index}`}><time>{timeText(item.timestamp)}</time><code>{item.method} {item.path}</code><b className={item.status >= 400 ? 'is-danger' : ''}>{item.status}</b><span>{fmt(item.durationMs)} ms</span></article>)}</div>
        </section>
      </>}
    </main>
  );
}

export default SystemMonitor;
